import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Paperclip, Send, Loader2, Trash2, CheckCheck, CloudUpload, CloudOff } from 'lucide-react';
import { MAIL_API, mailAuth, fmtSize, getMailbox } from './mailUtils';
import { RecipientInput, joinAddrs } from './RecipientInput';
import { SubjectSuggest } from './SubjectSuggest';
import RichEditor from './RichEditor';
import { TemplatePicker } from './MailTemplates';
import { useAuth } from '../../App';

export const buildDraft = (mode, message, me, replyAll = false) => {
  if (!message) return { mode: 'new', to: '', cc: '', subject: '' };
  const meL = (me || '').toLowerCase();
  if (mode === 'forward') return { mode, to: '', cc: '', subject: /^fwd?:/i.test(message.subject || '') ? message.subject : `Fwd: ${message.subject || ''}`, message_id: message.id, thread_id: message.thread_id, attachments: message.attachments || [] };
  const others = [...(message.to || []), ...(message.cc || [])].filter(a => a.email.toLowerCase() !== meL);
  const replyTo = message.mine ? (message.to || []) : [message.from];
  return { mode: 'reply', to: joinAddrs(replyTo), cc: replyAll ? joinAddrs(others.filter(a => !replyTo.some(r => r.email === a.email))) : '',
    subject: /^re:/i.test(message.subject || '') ? message.subject : `Re: ${message.subject || ''}`, message_id: message.id, thread_id: message.thread_id };
};

const esc = (s) => (s || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
export const textToHtml = (text) => (text || '').split(/\n{2,}/).map(p => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');

const AUTOSAVE_MS = 2500;

export const MailComposer = ({ draft, onClose, onSent, onDiscarded }) => {
  const { user } = useAuth();
  const [to, setTo] = useState(draft.to || '');
  const [cc, setCc] = useState(draft.cc || '');
  const [bcc, setBcc] = useState(draft.bcc || '');
  const [showCc, setShowCc] = useState(Boolean(draft.cc || draft.bcc));
  const [subject, setSubject] = useState(draft.subject || '');
  const [bodyHtml, setBodyHtml] = useState(draft.body_html || (draft.body ? textToHtml(draft.body) : ''));
  const [files, setFiles] = useState([]);
  const [includeAtts, setIncludeAtts] = useState(true);
  const [sending, setSending] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [draftId, setDraftId] = useState(draft.draft_id || '');
  const [saveState, setSaveState] = useState(draft.draft_id ? 'saved' : 'idle'); // idle | dirty | saving | saved | error
  const [savedAt, setSavedAt] = useState(null);
  const fileRef = useRef(null);
  const edRef = useRef(null);
  const timer = useRef(null);
  const latest = useRef({});
  const draftIdRef = useRef(draft.draft_id || '');
  const closed = useRef(false);
  useEffect(() => { latest.current = { to, cc, bcc, subject, bodyHtml }; }, [to, cc, bcc, subject, bodyHtml]);
  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

  // ── Gmail-synced autosave ──
  const inflight = useRef(null);
  const saveDraft = useCallback(async () => {
    if (inflight.current) await inflight.current;
    const v = latest.current;
    const hasContent = v.to.trim() || v.subject.trim() || v.bodyHtml.replace(/<[^>]+>/g, '').trim() || /<img/i.test(v.bodyHtml);
    if (!hasContent || closed.current) return null;
    setSaveState('saving');
    let done; inflight.current = new Promise(res => { done = res; });
    try {
      const r = await axios.put(`${MAIL_API}/drafts`, { draft_id: draftIdRef.current, to: v.to, cc: v.cc, bcc: v.bcc, subject: v.subject, body_html: v.bodyHtml,
        mode: draft.mode, message_id: draft.message_id || '', thread_id: draft.thread_id || '' }, mailAuth());
      if (r.data.draft_id) { setDraftId(r.data.draft_id); draftIdRef.current = r.data.draft_id; }
      baseline.current = { ...v };
      setSavedAt(r.data.saved_at ? new Date(r.data.saved_at) : new Date());
      setSaveState('saved');
      return r.data.draft_id;
    } catch (e) {
      setSaveState('error');
      return null;
    } finally { inflight.current = null; done(); }
  }, [draft.mode, draft.message_id, draft.thread_id]);

  // dirty = differs from what we opened with (or last saved) — so opening Reply/Forward and closing never creates a draft
  const baseline = useRef({ to: draft.to || '', cc: draft.cc || '', bcc: draft.bcc || '', subject: draft.subject || '', bodyHtml: draft.body_html || (draft.body ? textToHtml(draft.body) : '') });
  const norm = (v) => (v || '').replace(/\s+/g, ' ').replace(/> </g, '><').trim();
  const isDirty = (v) => ['to', 'cc', 'bcc', 'subject', 'bodyHtml'].some(k => norm(v[k]) !== norm(baseline.current[k]));
  useEffect(() => {
    if (!isDirty({ to, cc, bcc, subject, bodyHtml })) return undefined;
    setSaveState(s => (s === 'saving' ? s : 'dirty'));
    clearTimeout(timer.current);
    timer.current = setTimeout(saveDraft, AUTOSAVE_MS);
    return () => clearTimeout(timer.current);
  }, [to, cc, bcc, subject, bodyHtml, saveDraft]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || document.querySelector('[data-testid="tpl-manager"], [data-testid="tpl-editor"]')) return;
      const el = document.activeElement;
      if (el?.tagName === 'INPUT' && el.value) { el.blur(); return; } // first Esc leaves a half-typed field alone
      close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const close = async () => {
    clearTimeout(timer.current);
    if (isDirty(latest.current)) await saveDraft();
    else if (inflight.current) await inflight.current;
    closed.current = true;
    onClose(!!draftIdRef.current);
  };
  const discard = async () => {
    if (draftIdRef.current && !window.confirm('Discard this draft? It will be removed from Gmail too.')) return;
    clearTimeout(timer.current);
    closed.current = true;
    const did = draftIdRef.current;
    if (did) {
      try { await axios.delete(`${MAIL_API}/drafts/${did}`, mailAuth()); } catch (e) { /* already gone */ }
      onDiscarded?.(did);
    }
    toast.success('Draft discarded');
    onClose();
  };

  const send = async () => {
    if (!to.trim()) { toast.error('Add a recipient'); return; }
    if (/\{\{\s*\w+\s*\}\}/.test(bodyHtml) || /\{\{\s*\w+\s*\}\}/.test(subject)) { toast.error('Fill in the red {{placeholders}} from the template first'); return; }
    clearTimeout(timer.current);
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('to', to); fd.append('cc', cc); fd.append('bcc', bcc); fd.append('subject', subject);
      fd.append('body', edRef.current?.getText() || ''); fd.append('body_html', bodyHtml);
      fd.append('mode', draft.mode); fd.append('thread_id', draft.thread_id || ''); fd.append('message_id', draft.message_id || '');
      fd.append('include_attachments', draft.mode === 'forward' && includeAtts ? '1' : '0');
      fd.append('draft_id', draftIdRef.current || ''); fd.append('template_id', templateId || '');
      files.forEach(f => fd.append('files', f));
      const r = await axios.post(`${MAIL_API}/send`, fd, { ...mailAuth(), timeout: 120000 });
      toast.success(draft.mode === 'new' ? 'Sent' : draft.mode === 'reply' ? 'Reply sent' : 'Forwarded');
      if (r.data.thread_id && draft.mode === 'reply') {
        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/daily-brief/done-thread`, { thread_id: r.data.thread_id }, mailAuth()).catch(() => {});
      }
      closed.current = true;
      onSent?.(r.data);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? `${d.message} ${d.action || ''}` : d || 'Could not send');
    }
    setSending(false);
  };

  const insertTemplate = ({ body_html, subject: subj, id }) => {
    const empty = edRef.current?.isEmpty();
    if (empty) setBodyHtml(body_html); else edRef.current?.appendHtml(body_html);
    if (subj && !subject.trim()) setSubject(subj);
    setTemplateId(id);
    toast.success('Template inserted — placeholders filled where we could');
  };

  const title = draft.mode === 'reply' ? 'Reply' : draft.mode === 'forward' ? 'Forward' : draft.draft_id ? 'Draft' : 'New message';
  const field = 'w-full bg-transparent border-b border-slate-200 px-1 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-sky-500';
  const stamp = savedAt ? savedAt.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' }) : '';
  const saveLabel = { idle: '', dirty: 'Unsaved changes', saving: 'Saving to Gmail drafts…', saved: `Saved to Drafts${stamp ? ` · ${stamp}` : ''}`, error: 'Could not save draft' }[saveState];
  const SaveIcon = saveState === 'saving' ? CloudUpload : saveState === 'error' ? CloudOff : CheckCheck;

  return createPortal(
    <div className="fixed inset-0 z-[9000] bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={close} data-testid="mail-composer-overlay">
      <div className="w-full sm:max-w-3xl bg-white border border-slate-200 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[94vh]" onClick={e => e.stopPropagation()} data-testid="mail-composer">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">{title}{getMailbox() && <span className="ml-2 normal-case tracking-normal font-semibold text-emerald-700" data-testid="mail-compose-from">· sending as {getMailbox()}</span>}</h3>
          <div className="flex items-center gap-2">
            {saveLabel && <span className={`hidden sm:inline-flex items-center gap-1 text-[11px] ${saveState === 'error' ? 'text-red-600' : 'text-slate-500'}`} data-testid="mail-draft-status" data-state={saveState}><SaveIcon className={`w-3.5 h-3.5 ${saveState === 'saving' ? 'animate-pulse' : ''}`} />{saveLabel}</span>}
            <button onClick={close} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Close (keeps the draft)" data-testid="mail-composer-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="px-4 pt-2 space-y-1 overflow-y-auto">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0"><RecipientInput value={to} onChange={setTo} placeholder="To — type a name, company or @domain" testid="mail-compose-to" autoFocus={draft.mode === 'new' && !draft.draft_id} /></div>
            {!showCc && <button onClick={() => setShowCc(true)} className="text-[11px] text-slate-500 hover:text-slate-900 shrink-0 mt-3" data-testid="mail-compose-cc-toggle">Cc/Bcc</button>}
          </div>
          {showCc && <RecipientInput value={cc} onChange={setCc} placeholder="Cc" testid="mail-compose-cc" />}
          {showCc && <RecipientInput value={bcc} onChange={setBcc} placeholder="Bcc" testid="mail-compose-bcc" />}
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className={field} data-testid="mail-compose-subject" />
          <SubjectSuggest to={to} cc={cc} subject={subject} chips={draft.chips || []} onPick={setSubject} />
          <div className="pt-2">
            <RichEditor ref={edRef} value={bodyHtml} onChange={setBodyHtml} placeholder="Write your message… paste or drop images right in" autoFocus={draft.mode !== 'new' || !!draft.draft_id} minHeight={draft.mode === 'new' ? 220 : 170} testid="mail-compose-body" />
          </div>
          {draft.mode !== 'new' && <p className="text-[11px] text-slate-500 pt-1 pb-1">{draft.ai ? `Drafted by ops from the thread + today's brief — read it over, tweak, then send. ` : ''}{draft.mode === 'reply' ? 'The original message is quoted below your reply, like Gmail. Your Gmail signature is added automatically.' : 'The forwarded message is included below. Your Gmail signature is added automatically.'}</p>}
          {draft.mode === 'forward' && draft.attachments?.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-slate-700 pb-1"><input type="checkbox" checked={includeAtts} onChange={e => setIncludeAtts(e.target.checked)} data-testid="mail-compose-include-atts" />Include the original {draft.attachments.length} attachment{draft.attachments.length > 1 ? 's' : ''}</label>
          )}
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pb-2" data-testid="mail-compose-files">
              {files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-slate-800">
                  <Paperclip className="w-3 h-3" />{f.name} <span className="text-slate-500">{fmtSize(f.size)}</span>
                  <button onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                </span>
              ))}
              <span className="text-[10px] text-slate-400 self-center">Attachments go out with the send (they are not kept in the Gmail draft).</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 px-4 py-3 border-t border-slate-200 bg-slate-50/60 rounded-b-2xl">
          <button onClick={send} disabled={sending} className="px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 text-white text-sm font-bold flex items-center gap-2 shadow-sm" data-testid="mail-compose-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{sending ? 'Sending…' : 'Send'}
          </button>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={e => { setFiles(fs => [...fs, ...Array.from(e.target.files || [])]); e.target.value = ''; }} data-testid="mail-compose-file-input" />
          <button onClick={() => fileRef.current?.click()} className="p-2 rounded-xl hover:bg-slate-200/70 text-slate-600" title="Attach files" data-testid="mail-compose-attach"><Paperclip className="w-4 h-4" /></button>
          <TemplatePicker ctx={{ to, chips: draft.chips || [], myName: user?.full_name || '' }} onInsert={insertTemplate} currentBody={bodyHtml} currentSubject={subject} />
          <button onClick={discard} className="p-2 rounded-xl hover:bg-red-50 text-slate-500 hover:text-red-600" title="Discard draft" data-testid="mail-compose-discard"><Trash2 className="w-4 h-4" /></button>
          <span className="ml-auto text-[11px] text-slate-500 hidden sm:inline">Sent from your Gmail · shows in your Sent folder</span>
        </div>
      </div>
    </div>, document.body);
};
